import { ROLES } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';
import { useOrganizationSearch } from '@/features/staff/useOrganizationSearch';
import { STATUS_LABELS } from '@/lib/organization';
import { testIdPart } from '@/lib/testIds';

import { ROUTES } from '../routes';

/** One line in the search results, whatever it came from. */
export interface SearchOption {
  /** Unique among the options shown. */
  key: string;
  label: string;
  /** The heading it is listed under. */
  group: string;
  /** The smaller line beneath the label. */
  secondary?: string;
  /** Where choosing it goes. Absent for a line that is only a message. */
  path?: string;
  /** Shown, but cannot be chosen. */
  disabled?: boolean;
  /** The option's test ID. See docs/test-ids.md. */
  testId: string;
}

export const ORGANIZATIONS_GROUP = 'Organizations';
export const SEARCHING_ORGANIZATIONS = 'Searching organizations…';

/**
 * Results beyond the portal's own pages, for whoever has any.
 *
 * Today that is Activist staff in their own area, who find organizations by
 * name. Everybody else gets nothing here, so the search box does not ask a
 * feature's endpoint on behalf of a role that could not use it. A section
 * built later that has its own things to find adds them here.
 */
export function useSearchSources(query: string): SearchOption[] {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  const staff = user?.role === ROLES.ACTIVIST_STAFF && !viewing;
  const { organizations, searching } = useOrganizationSearch(query, staff);

  if (!staff) return [];
  if (searching) {
    return [
      {
        key: 'organizations-searching',
        label: SEARCHING_ORGANIZATIONS,
        group: ORGANIZATIONS_GROUP,
        disabled: true,
        testId: 'global-search-organizations-searching-option',
      },
    ];
  }
  return (organizations ?? []).map((organization) => ({
    key: `organization-${organization.id}`,
    label: organization.name,
    group: ORGANIZATIONS_GROUP,
    secondary: STATUS_LABELS[organization.status] ?? organization.status,
    path: ROUTES.staff.organization(organization.id),
    testId: `global-search-organization-${testIdPart(organization.id)}-option`,
  }));
}
